import { createI18n } from 'vue-i18n'
import fr from '@/locales/fr.json'
import en from '@/locales/en.json'
import es from '@/locales/es.json'
import { APP_NAMESPACE } from '@/meta'

/**
 * Internationalisation posée DÈS LE STARTER, avec deux langues.
 *
 * Ce n'est pas de l'anticipation gratuite : ajouter i18n à une application déjà
 * écrite, c'est rouvrir chaque composant pour en extraire les chaînes. Avec deux
 * langues en place dès le premier jour, la troisième ne coûte qu'un fichier
 * JSON, et un texte codé en dur se remarque immédiatement à la relecture.
 *
 * `legacy: false` : API de composition (`useI18n()`), la seule à jour sur
 * Vue 3 et la seule qui typera correctement `t()`.
 */

const STORAGE_KEY = `${APP_NAMESPACE}.locale`
const SUPPORTED = ['fr', 'es', 'en'] as const
export type Locale = (typeof SUPPORTED)[number]

function isSupported(value: string): value is Locale {
  return (SUPPORTED as readonly string[]).includes(value)
}

/**
 * Choix de la langue : préférence explicite mémorisée, sinon la langue du
 * navigateur, sinon le français. `navigator.language` vaut souvent « fr-BE » ou
 * « en-US » : on ne compare que la partie avant le tiret.
 */
function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && isSupported(stored)) return stored
  } catch {
    // Stockage indisponible : on continue sur la détection navigateur.
  }
  const nav = (navigator.language || '').split('-')[0] ?? ''
  return isSupported(nav) ? nav : 'fr'
}

export const i18n = createI18n({
  legacy: false,
  locale: detectLocale(),
  // Une clé absente d'une traduction retombe sur le français plutôt que
  // d'afficher la clé brute à l'écran.
  fallbackLocale: 'fr',
  messages: { fr, es, en },
  // Silence en production : les avertissements de clé manquante sont utiles au
  // développement, pas dans la console d'un utilisateur.
  missingWarn: import.meta.env.DEV,
  fallbackWarn: import.meta.env.DEV,
})

/** Change la langue et la mémorise. Met aussi à jour `<html lang>` (a11y, SEO). */
export function setLocale(locale: Locale): void {
  i18n.global.locale.value = locale
  document.documentElement.lang = locale
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch {
    // Non mémorisé, mais appliqué pour cette session.
  }
}

export const availableLocales = SUPPORTED

/**
 * Nom de chaque langue écrit DANS cette langue : quelqu'un qui ne lit pas la
 * langue courante doit pouvoir retrouver la sienne dans le sélecteur. Un seul
 * exemplaire, partagé par tous les sélecteurs : le `Record<Locale, …>` fait
 * échouer le typage si une langue ajoutée n'a pas de nom.
 */
export const localeNames: Record<Locale, string> = { fr: 'Français', es: 'Español', en: 'English' }
